import React from 'react';
import { Link } from 'react-router-dom';
import { 
  CheckIcon, 
  ArrowRightIcon,
  CameraIcon,
  CogIcon,
  ShieldCheckIcon,
  ChartBarIcon,
  ClockIcon,
  DocumentTextIcon,
  QrCodeIcon,
  ServerIcon
} from '@heroicons/react/24/outline';

export const DICOMCapturePage = () => {
  const features = [
    {
      icon: <CameraIcon className="h-8 w-8" />,
      title: "Captura Automática",
      benefits: [
        "Captura directa desde equipos médicos",
        "Detección automática de dispositivos",
        "Configuración plug-and-play",
        "Soporte para múltiples formatos de entrada"
      ]
    },
    {
      icon: <ShieldCheckIcon className="h-8 w-8" />,
      title: "Validación de Calidad",
      benefits: [
        "Verificación automática de calidad de imagen",
        "Detección de errores en tiempo real",
        "Corrección automática de metadatos",
        "Cumplimiento con estándares DICOM"
      ]
    },
    {
      icon: <ChartBarIcon className="h-8 w-8" />,
      title: "Procesamiento en Tiempo Real",
      benefits: [
        "Procesamiento instantáneo de imágenes",
        "Optimización automática de archivos",
        "Compresión inteligente",
        "Análisis de rendimiento en vivo"
      ]
    }
  ];

  const commercialFeatures = [
    {
      icon: <ServerIcon className="h-8 w-8" />,
      title: "Integración con PACS",
      description: "Conexión directa con sistemas PACS existentes",
      features: [
        "Conexión automática a servidores PACS",
        "Sincronización bidireccional de datos",
        "Manejo de errores y reintentos",
        "Monitoreo de estado de conexiones"
      ]
    },
    {
      icon: <CogIcon className="h-8 w-8" />,
      title: "Control de Flujo de Trabajo",
      description: "Gestión completa del proceso de captura",
      features: [
        "Configuración de workflows personalizados",
        "Automatización de procesos repetitivos",
        "Notificaciones en tiempo real",
        "Logs detallados de actividad"
      ]
    },
    {
      icon: <DocumentTextIcon className="h-8 w-8" />,
      title: "Gestión de Metadatos",
      description: "Manejo inteligente de información DICOM",
      features: [
        "Extracción automática de metadatos",
        "Validación de campos DICOM",
        "Corrección automática de errores",
        "Enriquecimiento de datos"
      ]
    },
    {
      icon: <QrCodeIcon className="h-8 w-8" />,
      title: "Sistema de Códigos QR",
      description: "Generación y lectura de códigos QR para identificación",
      features: [
        "Generación automática de códigos QR",
        "Lectura de códigos existentes",
        "Vinculación con datos de pacientes",
        "Integración con sistemas de identificación"
      ]
    }
  ];

  const benefits = [
    {
      category: "Eficiencia Operacional",
      icon: <ClockIcon className="h-6 w-6" />,
      benefits: [
        "Reducción del 70% en tiempo de captura",
        "Eliminación de errores manuales",
        "Procesamiento 24/7 sin supervisión",
        "Integración transparente con equipos existentes"
      ]
    },
    {
      category: "Calidad y Confiabilidad",
      icon: <ShieldCheckIcon className="h-6 w-6" />,
      benefits: [
        "Validación automática de calidad",
        "Cumplimiento 100% con estándares DICOM",
        "Backup automático de datos",
        "Recuperación ante fallos"
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-green-50 to-emerald-100 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              DICOMCapture
              <span className="text-green-600 block">Captura Inteligente</span>
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
              Herramienta especializada para la captura y procesamiento de imágenes DICOM 
              desde equipos médicos con validación automática y control de flujo de trabajo.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="border border-green-600 text-green-600 px-8 py-3 rounded-lg text-lg font-medium hover:bg-green-50 transition-colors duration-200"
              >
                Solicitar Demo
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Key Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Características Principales
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              DICOMCapture automatiza la captura y procesamiento de imágenes médicas, 
              proporcionando eficiencia operacional y control de calidad.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="bg-gray-50 rounded-lg p-8">
                <div className="text-green-600 mb-6 flex justify-center">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4 text-center">
                  {feature.title}
                </h3>
                <ul className="space-y-3">
                  {feature.benefits.map((benefit, benefitIndex) => (
                    <li key={benefitIndex} className="flex items-start">
                      <CheckIcon className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Commercial Features Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Funcionalidades Comerciales Específicas
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Herramientas especializadas diseñadas para optimizar 
              la captura y procesamiento en entornos médicos profesionales.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {commercialFeatures.map((feature, index) => (
              <div key={index} className="bg-white rounded-lg shadow-lg p-8">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="h-12 w-12 bg-green-100 rounded-lg flex items-center justify-center text-green-600">
                      {feature.icon}
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-gray-600 mb-4">
                      {feature.description}
                    </p>
                    <ul className="space-y-2">
                      {feature.features.map((item, itemIndex) => (
                        <li key={itemIndex} className="flex items-start">
                          <CheckIcon className="h-4 w-4 text-green-500 mr-2 mt-1 flex-shrink-0" />
                          <span className="text-sm text-gray-700">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Beneficios para el Cliente
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              DICOMCapture no solo mejora la eficiencia, sino que también 
              proporciona un retorno de inversión significativo.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {benefits.map((benefit, index) => (
              <div key={index} className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg p-8">
                <div className="flex items-center mb-6">
                  <div className="h-12 w-12 bg-green-600 rounded-lg flex items-center justify-center text-white mr-4">
                    {benefit.icon}
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">
                    {benefit.category}
                  </h3>
                </div>
                <ul className="space-y-4">
                  {benefit.benefits.map((item, itemIndex) => (
                    <li key={itemIndex} className="flex items-start">
                      <CheckIcon className="h-6 w-6 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ROI Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Retorno de Inversión Comprobado
              </h2>
              <p className="text-xl text-gray-600 mb-8">
                Los centros médicos que implementan DICOMCapture reportan mejoras 
                significativas en eficiencia y reducción de costos operativos.
              </p>
              <div className="space-y-6">
                <div className="flex items-center">
                  <div className="text-4xl font-bold text-green-600 mr-4">70%</div>
                  <div className="text-gray-700">Reducción en tiempo de captura</div>
                </div>
                <div className="flex items-center">
                  <div className="text-4xl font-bold text-blue-600 mr-4">24/7</div>
                  <div className="text-gray-700">Procesamiento automático</div>
                </div>
                <div className="flex items-center">
                  <div className="text-4xl font-bold text-purple-600 mr-4">100%</div>
                  <div className="text-gray-700">Cumplimiento DICOM</div>
                </div>
              </div>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-lg">
              <h3 className="text-xl font-semibold text-gray-900 mb-6 text-center">
                Casos de Uso Típicos
              </h3>
              <div className="space-y-4">
                <div className="border-l-4 border-green-500 pl-4">
                  <h4 className="font-semibold text-gray-900">Centros de Diagnóstico</h4>
                  <p className="text-gray-600 text-sm">Captura desde múltiples equipos simultáneamente</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <h4 className="font-semibold text-gray-900">Hospitales</h4>
                  <p className="text-gray-600 text-sm">Integración con sistemas PACS existentes</p>
                </div>
                <div className="border-l-4 border-purple-500 pl-4">
                  <h4 className="font-semibold text-gray-900">Clínicas Especializadas</h4>
                  <p className="text-gray-600 text-sm">Workflows personalizados por especialidad</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-green-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            ¿Listo para optimizar tu captura DICOM?
          </h2>
          <p className="text-xl text-green-100 mb-8 max-w-2xl mx-auto">
            Descubre cómo DICOMCapture puede transformar la eficiencia 
            de captura de imágenes en tu centro médico.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="border border-white text-white px-8 py-3 rounded-lg text-lg font-medium hover:bg-green-700 transition-colors duration-200"
            >
              Solicitar Demo Personalizada
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
